import { useEffect, useState } from "react";
import { Button, Text, ScrollBox, Image, LicensePlate } from "../Index";
import CloseIcon from "../../assets/Icon/close.svg";
import carFallback from "../../assets/Image/car-image.webp";
import "./vehicleSelectPopup.scss";

// Reusable popup: lists a customer's registered vehicles for the Service
// Worker to pick one. Pure presentational — data loading stays with the caller
// (customerApi.getCustomerVehicles), so it works with any vehicle source.
//
// Props:
//   isOpen, onClose, vehicles (array of backend vehicle rows), loading,
//   error, customerName, selectedVehicleId, onSelect(vehicle)
function VehicleSelectPopup({
  isOpen,
  onClose,
  vehicles = [],
  loading = false,
  error = null,
  customerName = "",
  selectedVehicleId = null,
  onSelect,
}) {
  const [tempSelectedId, setTempSelectedId] = useState(selectedVehicleId);

  useEffect(() => {
    if (isOpen) {
      setTempSelectedId(selectedVehicleId);
    }
  }, [isOpen, selectedVehicleId]);

  // Fixed popup: lock body scroll while open so the page never scrolls
  // and the card stays centered with stable dimensions.
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    const vehicle = vehicles.find((v) => String(v.id) === String(tempSelectedId));
    if (vehicle && onSelect) {
      onSelect(vehicle);
    }
  };

  const vehicleTitle = (v) => v.title || v.brand || "خودرو بدون نام";
  const vehicleMileage = (v) =>
    v.currentMileage !== undefined && v.currentMileage !== null
      ? Number(v.currentMileage).toLocaleString("fa-IR")
      : "۰";

  return (
    <div className="vehicle-select-pop-overlay" onClick={onClose}>
      <div
        className="vehicle-select-pop-container"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="انتخاب خودروی مشتری"
      >
        <div className="vehicle-select-pop-header">
          <Text FontWight="Bold" FontSize="lg" position="right">
            انتخاب خودرو
          </Text>
          {customerName && (
            <Text FontSize="sm" color="gray" position="right">
              خودروهای {customerName}
            </Text>
          )}
          <button
            className="vehicle-select-pop-close"
            onClick={onClose}
            aria-label="بستن"
            type="button"
          >
            <img src={CloseIcon} alt="" />
          </button>
        </div>

        <div className="vehicle-select-pop-scroll">
          <ScrollBox height="100%">
            <div className="vehicle-select-pop-list">
              {loading ? (
                <Text position="center" FontSize="md" color="gray">
                  در حال بارگذاری خودروها...
                </Text>
              ) : error ? (
                <Text position="center" FontSize="md" color="red">
                  {error}
                </Text>
              ) : vehicles.length === 0 ? (
                <Text position="center" FontSize="md" color="gray">
                  هیچ خودرویی برای این مشتری ثبت نشده است
                </Text>
              ) : (
                vehicles.map((vehicle) => (
                  <div
                    key={vehicle.id}
                    className={`vehicle-select-pop-card ${
                      String(tempSelectedId) === String(vehicle.id) ? "selected" : ""
                    }`}
                    onClick={() => setTempSelectedId(vehicle.id)}
                  >
                    <div className="vehicle-select-pop-main">
                      <div className="vehicle-select-pop-image-wrapper">
                        <Image src={carFallback} />
                      </div>
                      <div className="vehicle-select-pop-info">
                        <Text FontWight="Bold" FontSize="md" position="right">
                          {vehicleTitle(vehicle)}
                        </Text>
                        <Text FontSize="sm" position="right">
                          سال ساخت: {vehicle.buildYear || "نامشخص"}
                        </Text>
                        <Text FontSize="sm" position="right">
                          رنگ: {vehicle.color || "نامشخص"}
                        </Text>
                        <Text FontSize="sm" position="right">
                          کیلومتر: {vehicleMileage(vehicle)}
                        </Text>
                      </div>
                    </div>
                    <LicensePlate
                      Main1={vehicle.plate1 || ""}
                      Main2={vehicle.plate2 || ""}
                      Main3={vehicle.plate3 || ""}
                      End={vehicle.plateEnd || ""}
                      compact
                    />
                    {String(tempSelectedId) === String(vehicle.id) && (
                      <div className="vehicle-select-pop-checkmark" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                        </svg>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollBox>
        </div>

        <div className="vehicle-select-pop-footer">
          <Button
            size="full"
            color="dark"
            onClick={handleConfirm}
            disabled={!tempSelectedId || loading || vehicles.length === 0}
          >
            تایید خودرو
          </Button>
        </div>
      </div>
    </div>
  );
}

export default VehicleSelectPopup;
